import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CornerUpLeft,
  Eye,
  Info,
  Mic,
  Paperclip,
  Pencil,
  Phone,
  PhoneCall,
  SendHorizontal,
  SmilePlus,
  Undo2,
  Video,
  X,
} from 'lucide-react';
import type {
  ChatAttachmentUploadMetadata,
  ChatConversationSummary,
  ChatMessage,
  ChatReactionEmoji,
} from '@bits/shared/zod';
import { REACTION_EMOJI } from '@bits/domain';
import { api, ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errors';
import { chatKeys, useChat } from '@/lib/chat-provider';
import {
  appendMessage,
  attachmentLabel,
  buildThreadItems,
  compareIds,
  conversationTitle,
  formatBytes,
  formatClock,
  formatDuration,
  insertMention,
  lastOwnMessageId,
  mentionQueryAt,
  mentionsStillPresent,
  newClientMessageId,
  otherMemberIds,
  pastedImageName,
  prependOlder,
  reactedByMe,
  reactionTitle,
  replaceInPage,
  seenByNames,
  segmentBody,
  voiceNoteFileName,
  type MessagePage,
} from '@/lib/chat-model';
import { VoiceRecorder, type RecorderPhase } from '@/lib/voice-recorder';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AttachmentView } from './AttachmentView';
import { GroupSheet } from './GroupSheet';

const TYPING_THROTTLE_MS = 2_500;
const NEAR_BOTTOM_PX = 120;

export function Thread({ conversationId }: { conversationId: string }): React.ReactElement {
  const { meId, conversations, online, typing } = useChat();
  const fromList = conversations?.find((c) => c.id === conversationId);
  const detail = useQuery({
    queryKey: chatKeys.conversation(conversationId),
    queryFn: () => api.chatConversation(conversationId),
    enabled: conversations !== undefined && !fromList,
    retry: false,
  });
  const conversation = fromList ?? detail.data;

  if (!conversation) {
    if (detail.isError) {
      return (
        <Empty
          title="This conversation is not available"
          body="It may have been archived, or you are no longer a member."
        />
      );
    }
    return <Empty title="" body="Loading…" />;
  }
  return (
    <ThreadView
      key={conversation.id}
      conversation={conversation}
      meId={meId}
      online={online}
      typingIds={typing[conversation.id] ?? EMPTY}
    />
  );
}

const EMPTY: ReadonlySet<string> = new Set();

function Empty({ title, body }: { title: string; body: string }): React.ReactElement {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      {title ? <p className="font-medium">{title}</p> : null}
      <p className="text-sm text-[hsl(var(--muted-foreground))]">{body}</p>
      <Link to="/chat" className="mt-2 text-sm font-medium text-[hsl(var(--brand))] md:hidden">
        Back to chats
      </Link>
    </div>
  );
}

function ThreadView({
  conversation,
  meId,
  online,
  typingIds,
}: {
  conversation: ChatConversationSummary;
  meId: string | null;
  online: ReadonlySet<string>;
  typingIds: ReadonlySet<string>;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { call: currentCall, startCall, joinCall } = useChat();
  const title = conversationTitle(conversation, meId);
  const others = otherMemberIds(conversation, meId);
  const isGroup = conversation.kind === 'group';
  const archived = conversation.status === 'archived';
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [replyTo, setReplyTo] = React.useState<ChatMessage | null>(null);
  const [callError, setCallError] = React.useState<string | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const [dropped, setDropped] = React.useState<File[] | null>(null);
  const dragDepth = React.useRef(0);

  const hasFiles = (e: React.DragEvent): boolean => [...e.dataTransfer.types].includes('Files');
  const onDragEnter = (e: React.DragEvent): void => {
    if (archived || !hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = (): void => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e: React.DragEvent): void => {
    if (archived || !hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (files.length > 0) setDropped(files);
  };
  const activeCall = conversation.activeCall;
  const inThisCall =
    activeCall !== null &&
    activeCall.participants.some((p) => p.personId === meId && p.status === 'joined');
  const busyElsewhere = currentCall !== null && currentCall.phase !== 'ended';

  const place = (kind: 'audio' | 'video'): void => {
    setCallError(null);
    startCall(conversation.id, kind).catch((err: unknown) => setCallError(errorMessage(err)));
  };
  const join = (): void => {
    if (!activeCall) return;
    setCallError(null);
    joinCall(activeCall.id).catch((err: unknown) => setCallError(errorMessage(err)));
  };

  const page = useQuery({
    queryKey: chatKeys.messages(conversation.id),
    queryFn: () => api.chatMessages(conversation.id),
    retry: false,
  });
  const messages = page.data?.messages ?? [];

  const loadOlder = useMutation({
    mutationFn: () => api.chatMessages(conversation.id, messages[0]?.id),
    onSuccess: (older) => {
      queryClient.setQueryData<MessagePage>(chatKeys.messages(conversation.id), (current) =>
        current ? prependOlder(current, older) : older,
      );
    },
  });

  // Scrolling: stick to the bottom while the reader is there, keep their place
  // when they have scrolled up, and land on a deep-linked message once.
  const scroller = React.useRef<HTMLDivElement>(null);
  const stickToBottom = React.useRef(true);
  const lastCount = React.useRef(0);
  const jumpedToHash = React.useRef(false);

  const onScroll = (): void => {
    const el = scroller.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  React.useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || messages.length === 0) return;
    const hash = window.location.hash.slice(1);
    if (!jumpedToHash.current && hash && messages.some((m) => m.id === hash)) {
      jumpedToHash.current = true;
      document.getElementById(`msg-${hash}`)?.scrollIntoView({ block: 'center' });
      stickToBottom.current = false;
    } else if (stickToBottom.current || lastCount.current === 0) {
      el.scrollTop = el.scrollHeight;
    }
    lastCount.current = messages.length;
  }, [messages]);

  // Read receipt: whatever is on screen while the tab is visible is read.
  const lastMarked = React.useRef<string | null>(conversation.myLastReadMessageId);
  const markRead = useMutation({ mutationFn: (id: string) => api.chatMarkRead(conversation.id, id) });
  const markReadRef = React.useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;
  const newestId = messages[messages.length - 1]?.id ?? null;
  React.useEffect(() => {
    const markVisible = (): void => {
      if (newestId === null || document.visibilityState !== 'visible') return;
      if (lastMarked.current !== null && compareIds(newestId, lastMarked.current) <= 0) return;
      lastMarked.current = newestId;
      markReadRef.current(newestId);
    };
    markVisible();
    document.addEventListener('visibilitychange', markVisible);
    return () => document.removeEventListener('visibilitychange', markVisible);
  }, [newestId]);

  const redact = useMutation({
    mutationFn: (messageId: string) => api.chatRedact(conversation.id, messageId),
  });
  const replaceMessage = (message: ChatMessage): void => {
    queryClient.setQueryData<MessagePage>(chatKeys.messages(conversation.id), (current) =>
      current ? replaceInPage(current, message) : current,
    );
  };
  const react = useMutation({
    mutationFn: (input: { messageId: string; emoji: ChatReactionEmoji }) =>
      api.chatToggleReaction(conversation.id, input.messageId, input.emoji),
    onSuccess: replaceMessage,
  });
  const [editing, setEditing] = React.useState<ChatMessage | null>(null);
  const [revisionsOf, setRevisionsOf] = React.useState<ChatMessage | null>(null);
  const memberNames = React.useMemo(
    () => new Map(conversation.members.map((m) => [m.personId, m.name])),
    [conversation.members],
  );

  const items = React.useMemo(() => buildThreadItems(messages), [messages]);
  const ownLastId = lastOwnMessageId(messages, meId);
  const seenBy = ownLastId ? seenByNames(conversation, ownLastId, meId) : [];
  const typingNames = conversation.members
    .filter((m) => typingIds.has(m.personId))
    .map((m) => m.name);

  const subtitle = isGroup
    ? `${conversation.members.length} members${archived ? ' · Archived' : ''}`
    : others.some((id) => online.has(id))
      ? 'Online'
      : 'Offline';

  return (
    <div
      className="relative flex h-full min-h-0 flex-col"
      onDragEnter={onDragEnter}
      onDragOver={(e) => {
        if (!archived && hasFiles(e)) e.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[hsl(var(--background))]/85">
          <p className="rounded-2xl border-2 border-dashed border-[hsl(var(--brand))] px-8 py-6 font-display text-lg font-semibold text-[hsl(var(--brand))]">
            Drop to send
          </p>
        </div>
      ) : null}
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[hsl(var(--border))]/70 px-3 sm:px-5">
        <Link
          to="/chat"
          aria-label="Back to chats"
          className="flex size-9 items-center justify-center rounded-xl hover:bg-[hsl(var(--muted))] md:hidden"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <Avatar
          name={title}
          personId={isGroup ? undefined : others[0]}
          group={isGroup}
          online={isGroup ? undefined : others.some((id) => online.has(id))}
        />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-lg font-semibold leading-tight tracking-tight">
            {title}
          </h2>
          <p className="truncate text-xs text-[hsl(var(--muted-foreground))]">{subtitle}</p>
        </div>
        {archived ? null : activeCall ? (
          inThisCall ? (
            <span className="flex items-center gap-1.5 rounded-full bg-[hsl(var(--accent))] px-3 py-1 text-xs font-medium text-[hsl(var(--accent-foreground))]">
              <PhoneCall className="size-3.5" />
              In call
            </span>
          ) : (
            <Button size="sm" onClick={join} disabled={busyElsewhere} aria-label="Join call">
              <PhoneCall />
              Join call
            </Button>
          )
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => place('audio')}
              disabled={busyElsewhere}
              aria-label="Start audio call"
            >
              <Phone />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => place('video')}
              disabled={busyElsewhere}
              aria-label="Start video call"
            >
              <Video />
            </Button>
          </>
        )}
        {isGroup ? (
          <Button size="sm" variant="ghost" onClick={() => setSheetOpen(true)} aria-label="Group details">
            <Info />
            <span className="hidden sm:inline">Details</span>
          </Button>
        ) : null}
      </header>
      {callError ? (
        <p className="border-b border-[hsl(var(--border))]/70 px-5 py-2 text-xs text-[hsl(var(--destructive))]">
          {callError}
        </p>
      ) : null}

      <div
        ref={scroller}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5"
        aria-label="Messages"
      >
        {page.isPending ? (
          <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
        ) : page.isError ? (
          <p className="text-center text-sm text-[hsl(var(--destructive))]">{errorMessage(page.error)}</p>
        ) : (
          <>
            {page.data?.hasMore ? (
              <div className="mb-3 flex justify-center">
                <Button size="sm" variant="outline" onClick={() => loadOlder.mutate()} disabled={loadOlder.isPending}>
                  {loadOlder.isPending ? 'Loading…' : 'Load earlier messages'}
                </Button>
              </div>
            ) : null}
            {messages.length === 0 ? (
              <p className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
                {isGroup ? 'No messages yet.' : `Say hello to ${title}.`}
              </p>
            ) : null}
            <ol className="flex flex-col">
              {items.map((item) =>
                item.kind === 'day' ? (
                  <li key={item.key} className="my-3 flex items-center gap-3" aria-label={item.label}>
                    <span className="h-px flex-1 bg-[hsl(var(--border))]" />
                    <span className="text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      {item.label}
                    </span>
                    <span className="h-px flex-1 bg-[hsl(var(--border))]" />
                  </li>
                ) : (
                  <MessageRow
                    key={item.key}
                    message={item.message}
                    mine={item.message.authorId !== null && item.message.authorId === meId}
                    meId={meId}
                    memberNames={memberNames}
                    firstInGroup={item.firstInGroup}
                    lastInGroup={item.lastInGroup}
                    showAuthor={isGroup}
                    seenBy={
                      item.message.authorId === meId && item.message.kind !== 'system'
                        ? seenByNames(conversation, item.message.id, meId)
                        : null
                    }
                    seenLine={item.message.id === ownLastId ? seenBy : null}
                    isGroup={isGroup}
                    onReply={archived ? null : () => setReplyTo(item.message)}
                    onReact={
                      archived || item.message.redacted || item.message.kind === 'system'
                        ? null
                        : (emoji) => react.mutate({ messageId: item.message.id, emoji })
                    }
                    onEdit={
                      !archived &&
                      item.message.authorId === meId &&
                      !item.message.redacted &&
                      (item.message.kind === 'text' || item.message.kind === 'file')
                        ? () => setEditing(item.message)
                        : null
                    }
                    onShowRevisions={() => setRevisionsOf(item.message)}
                    onRedact={
                      item.message.authorId === meId && !item.message.redacted
                        ? () => redact.mutate(item.message.id)
                        : null
                    }
                  />
                ),
              )}
            </ol>
          </>
        )}
      </div>

      <div className="h-5 shrink-0 px-5 text-xs text-[hsl(var(--muted-foreground))]">
        {typingNames.length > 0 ? (
          <span aria-live="polite">
            {typingNames.length === 1
              ? `${typingNames[0]} is typing…`
              : typingNames.length === 2
                ? `${typingNames[0]} and ${typingNames[1]} are typing…`
                : 'Several people are typing…'}
          </span>
        ) : null}
      </div>

      {archived ? (
        <p className="border-t border-[hsl(var(--border))]/70 px-5 py-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          This group is archived. Unarchive it from the details panel to keep talking.
        </p>
      ) : (
        <Composer
          conversation={conversation}
          meId={meId}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          droppedFiles={dropped}
          onDroppedHandled={() => setDropped(null)}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
          onEdited={(message) => {
            setEditing(null);
            replaceMessage(message);
          }}
          onSent={(message) => {
            stickToBottom.current = true;
            queryClient.setQueryData<MessagePage>(chatKeys.messages(conversation.id), (current) =>
              current ? appendMessage(current, message) : current,
            );
          }}
        />
      )}

      {isGroup ? (
        <GroupSheet conversation={conversation} open={sheetOpen} onOpenChange={setSheetOpen} />
      ) : null}
      <RevisionsDialog
        conversationId={conversation.id}
        message={revisionsOf}
        onClose={() => setRevisionsOf(null)}
      />
    </div>
  );
}

function RevisionsDialog({
  conversationId,
  message,
  onClose,
}: {
  conversationId: string;
  message: ChatMessage | null;
  onClose: () => void;
}): React.ReactElement {
  const revisions = useQuery({
    queryKey: ['chat', 'revisions', conversationId, message?.id ?? '', message?.editedAt ?? ''],
    queryFn: () => api.chatRevisions(conversationId, message!.id),
    enabled: message !== null,
    retry: false,
  });
  return (
    <Dialog open={message !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit history</DialogTitle>
          <DialogDescription>Every earlier version is kept. The current text is at the top.</DialogDescription>
        </DialogHeader>
        {message ? (
          <ol className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto text-sm" aria-label="Revisions">
            <li className="rounded-xl bg-[hsl(var(--accent))] px-3 py-2">
              <p className="whitespace-pre-wrap break-words">{message.body || <span className="italic">No caption</span>}</p>
              <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                Current{message.editedAt ? ` · edited ${formatClock(message.editedAt)}` : ''}
              </p>
            </li>
            {revisions.isPending ? (
              <li className="px-3 py-2 text-xs text-[hsl(var(--muted-foreground))]">Loading…</li>
            ) : revisions.isError ? (
              <li className="px-3 py-2 text-xs text-[hsl(var(--destructive))]">{errorMessage(revisions.error)}</li>
            ) : (
              revisions.data.map((r) => (
                <li key={r.id} className="rounded-xl bg-[hsl(var(--muted))] px-3 py-2">
                  <p className="whitespace-pre-wrap break-words">{r.body || <span className="italic">No caption</span>}</p>
                  <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                    Until {formatClock(r.supersededAt)}
                    {r.editedByName ? ` · changed by ${r.editedByName}` : ''}
                  </p>
                </li>
              ))
            )}
          </ol>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function MessageRow({
  message,
  mine,
  meId,
  memberNames,
  firstInGroup,
  lastInGroup,
  showAuthor,
  seenBy,
  seenLine,
  isGroup,
  onReply,
  onReact,
  onEdit,
  onShowRevisions,
  onRedact,
}: {
  message: ChatMessage;
  mine: boolean;
  meId: string | null;
  memberNames: ReadonlyMap<string, string>;
  firstInGroup: boolean;
  lastInGroup: boolean;
  showAuthor: boolean;
  seenBy: string[] | null;
  seenLine: string[] | null;
  isGroup: boolean;
  onReply: (() => void) | null;
  onReact: ((emoji: ChatReactionEmoji) => void) | null;
  onEdit: (() => void) | null;
  onShowRevisions: () => void;
  onRedact: (() => void) | null;
}): React.ReactElement {
  const [armed, setArmed] = React.useState(false);
  const [picking, setPicking] = React.useState(false);
  React.useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 5_000);
    return () => window.clearTimeout(timer);
  }, [armed]);
  React.useEffect(() => {
    if (!picking) return;
    const close = (): void => setPicking(false);
    const timer = window.setTimeout(() => document.addEventListener('click', close, { once: true }), 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', close);
    };
  }, [picking]);

  if (message.kind === 'system') {
    return (
      <li id={`msg-${message.id}`} className="my-2 text-center text-xs italic text-[hsl(var(--muted-foreground))]">
        {message.body}
      </li>
    );
  }

  return (
    <li
      id={`msg-${message.id}`}
      className={cn(
        'group flex scroll-mt-20 flex-col',
        mine ? 'items-end' : 'items-start',
        firstInGroup ? 'mt-3' : 'mt-0.5',
        'target:[&>div]:ring-2 target:[&>div]:ring-[hsl(var(--ring))]',
      )}
    >
      {firstInGroup && showAuthor && !mine ? (
        <span className="mb-0.5 px-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
          {message.authorName ?? 'Someone'}
        </span>
      ) : null}
      <div className={cn('flex max-w-[85%] items-end gap-1.5 sm:max-w-[70%]', mine ? 'flex-row-reverse' : 'flex-row')}>
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm',
            mine
              ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]'
              : 'bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] shadow-[0_1px_2px_rgba(20,20,20,0.06)]',
            mine && !firstInGroup ? 'rounded-tr-md' : '',
            mine && !lastInGroup ? 'rounded-br-md' : '',
            !mine && !firstInGroup ? 'rounded-tl-md' : '',
            !mine && !lastInGroup ? 'rounded-bl-md' : '',
          )}
        >
          {message.replyTo ? (
            <a
              href={`#${message.replyTo.id}`}
              className={cn(
                'mb-1.5 block rounded-lg border-l-2 px-2 py-1 text-xs',
                mine
                  ? 'border-[hsl(var(--brand-foreground))]/60 bg-[hsl(var(--brand-foreground))]/10'
                  : 'border-[hsl(var(--brand))] bg-[hsl(var(--accent))]',
              )}
            >
              <span className="block font-medium">{message.replyTo.authorName ?? 'Someone'}</span>
              <span className={cn('block truncate', message.replyTo.redacted ? 'italic' : '')}>
                {message.replyTo.redacted ? 'Message unsent' : message.replyTo.excerpt || 'Attachment'}
              </span>
            </a>
          ) : null}
          {message.redacted ? (
            <span className="italic opacity-80">Message unsent</span>
          ) : (
            <>
              <AttachmentView message={message} mine={mine} />
              {message.body ? <Body body={message.body} mine={mine} /> : null}
            </>
          )}
        </div>
        <div
          className={cn(
            'relative flex shrink-0 items-center gap-0.5 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100',
            picking ? 'opacity-100' : 'opacity-0',
          )}
        >
          {onReact ? (
            <button
              type="button"
              onClick={() => setPicking((p) => !p)}
              aria-label="React"
              aria-expanded={picking}
              className="rounded-lg p-1 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
            >
              <SmilePlus className="size-4" />
            </button>
          ) : null}
          {picking && onReact ? (
            <div
              role="group"
              aria-label="Pick a reaction"
              className={cn(
                'absolute bottom-full z-10 mb-1 flex gap-0.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1 shadow-lg',
                mine ? 'right-0' : 'left-0',
              )}
            >
              {REACTION_EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    setPicking(false);
                    onReact(emoji);
                  }}
                  aria-label={`React with ${emoji}`}
                  className="flex size-8 items-center justify-center rounded-full text-lg leading-none transition-transform hover:scale-125 hover:bg-[hsl(var(--muted))]"
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
          {onReply && !message.redacted ? (
            <button
              type="button"
              onClick={onReply}
              aria-label="Reply"
              className="rounded-lg p-1 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
            >
              <CornerUpLeft className="size-4" />
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit"
              className="rounded-lg p-1 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
            >
              <Pencil className="size-4" />
            </button>
          ) : null}
          {onRedact ? (
            <button
              type="button"
              onClick={() => {
                if (armed) onRedact();
                else setArmed(true);
              }}
              aria-label={armed ? 'Confirm unsend' : 'Unsend'}
              className={cn(
                'rounded-lg p-1 hover:bg-[hsl(var(--muted))]',
                armed ? 'text-[hsl(var(--destructive))] opacity-100' : 'text-[hsl(var(--muted-foreground))]',
              )}
            >
              {armed ? <span className="px-1 text-xs font-medium">Unsend?</span> : <Undo2 className="size-4" />}
            </button>
          ) : null}
        </div>
      </div>
      {message.reactions.length > 0 && !message.redacted ? (
        <ul className={cn('mt-1 flex flex-wrap gap-1 px-1', mine ? 'justify-end' : '')} aria-label="Reactions">
          {message.reactions.map((reaction) => {
            const own = reactedByMe(reaction, meId);
            return (
              <li key={reaction.emoji}>
                <button
                  type="button"
                  onClick={onReact ? () => onReact(reaction.emoji) : undefined}
                  disabled={!onReact}
                  title={reactionTitle(reaction, memberNames, meId)}
                  aria-label={reactionTitle(reaction, memberNames, meId)}
                  aria-pressed={own}
                  className={cn(
                    'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors disabled:cursor-default',
                    own
                      ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                      : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))]',
                  )}
                >
                  <span className="text-sm leading-none">{reaction.emoji}</span>
                  <span className="tabular-nums">{reaction.count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {lastInGroup || message.editedAt ? (
        <span className="mt-0.5 flex items-center gap-1 px-1 text-[11px] text-[hsl(var(--muted-foreground))]">
          {lastInGroup ? formatClock(message.createdAt) : null}
          {message.editedAt && !message.redacted ? (
            <button
              type="button"
              onClick={onShowRevisions}
              className="italic underline-offset-2 hover:underline"
              aria-label="Edited; show history"
            >
              {lastInGroup ? '· edited' : 'edited'}
            </button>
          ) : null}
          {seenLine && seenLine.length > 0 ? (
            <span>{isGroup ? `· Seen by ${seenLine.join(', ')}` : '· Seen'}</span>
          ) : seenBy && seenBy.length > 0 && lastInGroup ? (
            <Eye
              className="size-3"
              aria-label={`Seen by ${seenBy.join(', ')}`}
              role="img"
            />
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

function Body({ body, mine }: { body: string; mine: boolean }): React.ReactElement {
  return (
    <span className="whitespace-pre-wrap break-words">
      {segmentBody(body).map((segment, i) =>
        segment.kind === 'link' ? (
          <a
            key={i}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn('underline underline-offset-2', mine ? '' : 'text-[hsl(var(--brand))]')}
          >
            {segment.href}
          </a>
        ) : (
          <React.Fragment key={i}>{segment.text}</React.Fragment>
        ),
      )}
    </span>
  );
}

function Composer({
  conversation,
  meId,
  replyTo,
  onClearReply,
  onSent,
  droppedFiles,
  onDroppedHandled,
  editing,
  onCancelEdit,
  onEdited,
}: {
  conversation: ChatConversationSummary;
  meId: string | null;
  replyTo: ChatMessage | null;
  onClearReply: () => void;
  onSent: (message: ChatMessage) => void;
  droppedFiles: File[] | null;
  onDroppedHandled: () => void;
  editing: ChatMessage | null;
  onCancelEdit: () => void;
  onEdited: (message: ChatMessage) => void;
}): React.ReactElement {
  const [text, setText] = React.useState('');
  const [caret, setCaret] = React.useState(0);
  const [picked, setPicked] = React.useState<Map<string, string>>(new Map());
  const [highlight, setHighlight] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState<string | null>(null);
  const [recorderPhase, setRecorderPhase] = React.useState<RecorderPhase>('idle');
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [recorder] = React.useState(() => new VoiceRecorder(setRecorderPhase));
  const textarea = React.useRef<HTMLTextAreaElement>(null);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const lastTypingAt = React.useRef(0);
  const pendingCaret = React.useRef<number | null>(null);
  const canRecord = VoiceRecorder.supported();
  const recording = recorderPhase === 'recording' || recorderPhase === 'starting';

  const mention = mentionQueryAt(text, caret);
  const candidates = mention
    ? conversation.members
        .filter((m) => m.personId !== meId && m.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, 6)
    : [];

  React.useEffect(() => {
    setHighlight(0);
  }, [mention?.query]);

  React.useEffect(() => {
    if (replyTo) textarea.current?.focus();
  }, [replyTo]);

  // Entering edit mode loads the current text; leaving it (or switching to
  // another message) drops whatever was typed for the edit.
  const draftBeforeEdit = React.useRef('');
  React.useEffect(() => {
    if (editing) {
      draftBeforeEdit.current = text;
      setText(editing.body);
      setPicked(new Map());
      setPending(null);
      setError(null);
      const el = textarea.current;
      el?.focus();
      pendingCaret.current = editing.body.length;
    } else {
      setText(draftBeforeEdit.current);
      draftBeforeEdit.current = '';
    }
  }, [editing?.id]);

  React.useEffect(() => () => recorder.cancel(), [recorder]);

  React.useEffect(() => {
    if (recorderPhase !== 'recording') {
      setElapsedMs(0);
      return;
    }
    const timer = window.setInterval(() => setElapsedMs(recorder.elapsedMs()), 250);
    return () => window.clearInterval(timer);
  }, [recorderPhase, recorder]);

  React.useEffect(() => {
    if (recorderPhase === 'denied') {
      setError('Microphone access was refused. Allow it in the browser and try again.');
    } else if (recorderPhase === 'unsupported') {
      setError('This browser cannot record voice notes.');
    }
  }, [recorderPhase]);

  React.useLayoutEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    if (pendingCaret.current !== null) {
      el.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [text]);

  const send = useMutation({
    mutationFn: (input: { body: string; mentions: string[]; replyToId?: string }) =>
      api.chatSend(conversation.id, {
        body: input.body,
        clientMessageId: newClientMessageId(),
        ...(input.replyToId ? { replyToId: input.replyToId } : {}),
        ...(input.mentions.length > 0 ? { mentions: input.mentions } : {}),
      }),
    onSuccess: (message) => {
      onSent(message);
      onClearReply();
    },
    onError: (err, input) => {
      setText(input.body);
      setError(
        err instanceof ApiRequestError && err.status === 429
          ? 'You are sending too quickly. Wait a moment.'
          : errorMessage(err),
      );
    },
  });
  const edit = useMutation({
    mutationFn: (input: { messageId: string; body: string; mentions: string[] }) =>
      api.chatEditMessage(conversation.id, input.messageId, {
        body: input.body,
        mentions: input.mentions,
      }),
    onSuccess: (message) => {
      setText('');
      setPicked(new Map());
      onEdited(message);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const busy = send.isPending || uploading !== null || edit.isPending;

  const upload = (file: Blob, fileName: string, metadata: ChatAttachmentUploadMetadata) =>
    api.chatUploadAttachment(conversation.id, metadata, file, fileName);

  // Several files go out as several messages, in order; the caption, reply
  // and mentions belong to the first one. A failure stops the run and says so.
  const sendFiles = async (files: File[], caption: string, mentions: string[]): Promise<void> => {
    setError(null);
    const reply = replyTo;
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]!;
        setUploading(files.length > 1 ? `Sending ${i + 1} of ${files.length}…` : `Sending ${file.name}…`);
        const message = await upload(file, file.name, {
          kind: 'file',
          clientMessageId: newClientMessageId(),
          ...(i === 0 && caption ? { caption } : {}),
          ...(i === 0 && mentions.length > 0 ? { mentions } : {}),
          ...(i === 0 && reply ? { replyToId: reply.id } : {}),
        });
        onSent(message);
      }
      onClearReply();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(null);
      textarea.current?.focus();
    }
  };

  // One file waits for a caption; more than one, or a file on top of one
  // already waiting, send straight away.
  const acceptFiles = (files: File[]): void => {
    const list = files.filter((f) => f.size > 0);
    if (list.length === 0) return;
    if (list.length === 1 && pending === null) {
      setPending(list[0]!);
      textarea.current?.focus();
      return;
    }
    void sendFiles(list, '', []);
  };
  const acceptRef = React.useRef(acceptFiles);
  acceptRef.current = acceptFiles;

  React.useEffect(() => {
    if (!droppedFiles) return;
    acceptRef.current(droppedFiles);
    onDroppedHandled();
  }, [droppedFiles, onDroppedHandled]);

  const submit = (): void => {
    if (busy) return;
    const body = text.trim();
    if (editing) {
      if (editing.kind === 'text' && !body) return;
      setError(null);
      edit.mutate({
        messageId: editing.id,
        body,
        mentions: [...new Set([...editing.mentions, ...mentionsStillPresent(body, picked)])],
      });
      return;
    }
    if (pending) {
      const file = pending;
      const mentions = mentionsStillPresent(body, picked);
      setPending(null);
      setText('');
      setPicked(new Map());
      void sendFiles([file], body, mentions);
      return;
    }
    if (!body) return;
    setError(null);
    setText('');
    setPicked(new Map());
    send.mutate({
      body,
      mentions: mentionsStillPresent(body, picked),
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    });
    textarea.current?.focus();
  };

  const finishRecording = async (): Promise<void> => {
    const recordingResult = await recorder.stop();
    if (!recordingResult) return;
    setError(null);
    setUploading('Sending voice note…');
    try {
      const message = await upload(recordingResult.blob, voiceNoteFileName(recordingResult.mimeType), {
        kind: 'voice',
        clientMessageId: newClientMessageId(),
        durationMs: Math.max(1, Math.round(recordingResult.durationMs)),
      });
      onSent(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const choose = (personId: string, name: string): void => {
    if (!mention) return;
    const next = insertMention(text, mention.start, caret, name);
    pendingCaret.current = next.caret;
    setText(next.text);
    setCaret(next.caret);
    setPicked((current) => new Map(current).set(personId, name));
  };

  const noteTyping = (): void => {
    const now = Date.now();
    if (now - lastTypingAt.current < TYPING_THROTTLE_MS) return;
    lastTypingAt.current = now;
    void api.chatTyping(conversation.id).catch(() => undefined);
  };

  const title = conversationTitle(conversation, meId);

  return (
    <div className="relative shrink-0 border-t border-[hsl(var(--border))]/70 px-3 pb-3 pt-2 sm:px-5">
      {editing ? (
        <div className="mb-2 flex items-center gap-2 rounded-xl border-l-2 border-[hsl(var(--brand))] bg-[hsl(var(--accent))] px-3 py-1.5 text-xs">
          <Pencil className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">
              {editing.kind === 'file' ? 'Editing caption' : 'Editing message'}
            </span>
            <span className="block truncate text-[hsl(var(--muted-foreground))]">
              Enter saves, Esc cancels. Earlier versions stay in the history.
            </span>
          </span>
          <button type="button" onClick={onCancelEdit} aria-label="Cancel edit" className="rounded-lg p-1 hover:bg-[hsl(var(--brand))]/10">
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      {replyTo && !editing ? (
        <div className="mb-2 flex items-center gap-2 rounded-xl border-l-2 border-[hsl(var(--brand))] bg-[hsl(var(--accent))] px-3 py-1.5 text-xs">
          <span className="min-w-0 flex-1">
            <span className="block font-medium">Replying to {replyTo.authorName ?? 'Someone'}</span>
            <span className="block truncate text-[hsl(var(--muted-foreground))]">
              {replyTo.body || attachmentLabel(replyTo) || ''}
            </span>
          </span>
          <button type="button" onClick={onClearReply} aria-label="Cancel reply" className="rounded-lg p-1 hover:bg-[hsl(var(--brand))]/10">
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      {pending ? (
        <div className="mb-2 flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-1.5 text-xs">
          <Paperclip className="size-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">{pending.name}</span>
            <span className="text-[hsl(var(--muted-foreground))]"> · {formatBytes(pending.size)}</span>
          </span>
          <button
            type="button"
            onClick={() => setPending(null)}
            aria-label="Remove attachment"
            className="rounded-lg p-1 hover:bg-[hsl(var(--muted))]"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      {mention && candidates.length > 0 ? (
        <ul
          role="listbox"
          aria-label="Mention someone"
          className="absolute bottom-full left-3 z-10 mb-1 w-64 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg sm:left-5"
        >
          {candidates.map((m, i) => (
            <li key={m.personId} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(m.personId, m.name)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                  i === highlight ? 'bg-[hsl(var(--accent))]' : 'hover:bg-[hsl(var(--muted))]',
                )}
              >
                <Avatar name={m.name} personId={m.personId} size="sm" />
                {m.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {recording ? (
        <div
          role="status"
          aria-label="Recording voice note"
          className="flex h-11 items-center gap-3 rounded-2xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 text-sm"
        >
          <span className="size-2.5 animate-pulse rounded-full bg-[hsl(var(--destructive))]" />
          <span className="flex-1 tabular-nums">
            {recorderPhase === 'starting' ? 'Starting…' : `Recording · ${formatDuration(elapsedMs / 1000)}`}
          </span>
          <Button type="button" size="sm" variant="ghost" onClick={() => recorder.cancel()} aria-label="Cancel recording">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void finishRecording()}
            disabled={recorderPhase !== 'recording'}
            aria-label="Send voice note"
          >
            <SendHorizontal />
            Send
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            aria-label="Choose files"
            onChange={(e) => {
              acceptFiles([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
          {editing ? null : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              aria-label="Attach a file"
              className="size-11 shrink-0 px-0"
            >
              <Paperclip />
            </Button>
          )}
          <textarea
            ref={textarea}
            value={text}
            rows={1}
            maxLength={4000}
            placeholder={
              editing
                ? editing.kind === 'file'
                  ? 'Caption (leave empty to remove it)'
                  : 'Edit your message'
                : pending
                  ? 'Add a caption (optional)'
                  : `Message ${title}`
            }
            aria-label="Message"
            onChange={(e) => {
              setText(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
              if (e.target.value.trim()) noteTyping();
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onPaste={(e) => {
              const files = [...e.clipboardData.files];
              if (files.length === 0) return;
              e.preventDefault();
              acceptFiles(
                files.map((f) =>
                  f.type.startsWith('image/') && (!f.name || /^image\.\w+$/i.test(f.name))
                    ? new File([f], pastedImageName(f.type), { type: f.type })
                    : f,
                ),
              );
            }}
            onKeyDown={(e) => {
              if (mention && candidates.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlight((h) => (h + 1) % candidates.length);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlight((h) => (h - 1 + candidates.length) % candidates.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  const pick = candidates[highlight];
                  if (pick) choose(pick.personId, pick.name);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setCaret(-1);
                  return;
                }
              }
              if (e.key === 'Escape' && editing) {
                e.preventDefault();
                onCancelEdit();
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            className="max-h-40 min-h-11 flex-1 resize-none rounded-2xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
          />
          {!text.trim() && !pending && !editing && canRecord ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setError(null);
                void recorder.start();
              }}
              disabled={busy}
              aria-label="Record a voice note"
              className="size-11 shrink-0 px-0"
            >
              <Mic />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submit}
              disabled={(!text.trim() && !pending && !(editing && editing.kind === 'file')) || busy}
              aria-label={editing ? 'Save edit' : 'Send'}
              className="size-11 shrink-0 px-0"
            >
              {editing ? <Pencil /> : <SendHorizontal />}
            </Button>
          )}
        </div>
      )}
      {uploading ? (
        <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]" aria-live="polite">
          {uploading}
        </p>
      ) : null}
      {error ? <p className="mt-1.5 text-xs text-[hsl(var(--destructive))]">{error}</p> : null}
    </div>
  );
}
